import React from 'react';
import { Card, CardBody, CardTitle, Row, Col, Spinner } from 'reactstrap';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler, // Required for 'fill: true' in datasets
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Helper to format date labels (remains the same)
const formatDateLabel = (timestamp) => {
  return new Date(timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// Define a color palette for consistency
const COLORS = {
  totalPoints: { border: 'rgb(107, 114, 128)', bg: 'rgba(107, 114, 128, 0.3)' },      // Gray
  commission: { border: 'rgb(59, 130, 246)', bg: 'rgba(59, 130, 246, 0.3)' },       // Blue
  avgDealValue: { border: 'rgb(249, 115, 22)', bg: 'rgba(249, 115, 22, 0.3)' },   // Orange
  listings: { border: 'rgb(34, 197, 94)', bg: 'rgba(34, 197, 94, 0.3)' },         // Green
  timeToFirstAction: { border: 'rgb(239, 68, 68)', bg: 'rgba(239, 68, 68, 0.3)' }, // Red
  activities: { border: 'rgb(168, 85, 247)', bg: 'rgba(168, 85, 247, 0.3)' },     // Purple
  white: '#FFFFFF',
};

// Enhanced Chart Options
const getChartOptions = (yAxisLabel) => ({
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        precision: 0,
        color: '#4b5563', // Gray-600
        font: {
          family: "'Inter', sans-serif", // Ensure 'Inter' font is loaded in your project
        },
      },
      grid: {
        color: '#e5e7eb', // Gray-200
        borderColor: '#d1d5db', // Gray-300
      },
      title: { // Optional Y-axis title
        display: !!yAxisLabel,
        text: yAxisLabel,
        color: '#374151', // Gray-700
        font: {
          family: "'Inter', sans-serif",
          size: 12,
          weight: '500',
        },
      }
    },
    x: {
      ticks: {
        maxRotation: 45,
        minRotation: 0,
        color: '#4b5563', // Gray-600
        font: {
          family: "'Inter', sans-serif",
        },
      },
      grid: {
        display: false, // Cleaner look without vertical grid lines
      },
    },
  },
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        color: '#374151', // Gray-700
        font: {
          family: "'Inter', sans-serif",
          size: 12,
        },
        padding: 20,
        usePointStyle: true,
        pointStyle: 'circle',
      },
    },
    tooltip: {
      mode: 'index',
      intersect: false,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      titleColor: COLORS.white,
      bodyColor: COLORS.white,
      titleFont: {
        family: "'Inter', sans-serif",
        size: 14,
        weight: 'bold',
      },
      bodyFont: {
        family: "'Inter', sans-serif",
        size: 12,
      },
      padding: 10,
      cornerRadius: 4,
      caretPadding: 10,
      // Example callback for currency (uncomment and adapt if needed)
      // callbacks: {
      //   label: function(context) {
      //     let label = context.dataset.label || '';
      //     if (label) {
      //       label += ': ';
      //     }
      //     if (context.parsed.y !== null) {
      //       if (context.dataset.label === 'Commission Value' || context.dataset.label === 'Average Deal Value') {
      //          label += new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(context.parsed.y);
      //       } else {
      //          label += context.parsed.y;
      //       }
      //     }
      //     return label;
      //   }
      // }
    },
  },
  elements: {
    line: {
      tension: 0.35, // Slightly smoother lines
      borderWidth: 2.5,
    },
    point: {
      radius: 0, // Hide points by default
      hoverRadius: 5,
      hitRadius: 15, // Easier to trigger tooltip
      backgroundColor: COLORS.white,
      hoverBorderWidth: 2,
    },
  },
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false,
  },
});



const PerformanceTrends = ({ historyData, isLoading, period }) => {
  if (isLoading) {
    return (
      <Card className="shadow-sm border-0" style={{ borderRadius: '0.75rem' }}>
        <CardBody className="p-4 d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '450px' }}>
          <Spinner color="primary" style={{ width: '3.5rem', height: '3.5rem' }} />
          <p className="mt-3 fs-5 text-muted">Loading Performance Data...</p>
        </CardBody>
      </Card>
    );
  }

  if (!historyData || historyData.length === 0) {
    return (
      <Card className="shadow-sm border-0" style={{ borderRadius: '0.75rem' }}>
        <CardBody className="p-4 d-flex flex-column justify-content-center align-items-center text-center" style={{ minHeight: '450px' }}>
          {/* Optional: Add an icon here, e.g., from react-icons */}
          {/* <SomeIcon size={50} className="text-muted mb-3" /> */}
          <CardTitle tag="h5" className="mb-2 text-primary">Performance Trends</CardTitle>
          <p className="text-muted mt-1">No historical data available for the selected period ({period}).</p>
          <p className="text-muted small">Try selecting a different time range or check again later.</p>
        </CardBody>
      </Card>
    );
  }

  const labels = historyData.map(d => formatDateLabel(d.timestamp));
  const commonChartOptions = getChartOptions(); // Base options

  const chartConfigs = [
    {
      title: 'Total Points',
      dataKey: 'totalPoints',
      borderColor: COLORS.totalPoints.border,
      backgroundColor: COLORS.totalPoints.bg,
      fill: true,
      yAxisLabel: 'Points'
    },
    {
      title: 'Commission Value',
      dataKey: 'commissionValue',
      borderColor: COLORS.commission.border,
      backgroundColor: COLORS.commission.bg,
      fill: true,
      yAxisLabel: 'Value ($)' // Example: Add currency if applicable
    },
    {
      title: 'Average Deal Value',
      dataKey: 'avgDealValue', // This will be a custom mapper
      borderColor: COLORS.avgDealValue.border,
      backgroundColor: COLORS.avgDealValue.bg,
      fill: false, // Often better for averages if overlaid
      yAxisLabel: 'Avg. Value ($)',
      dataMap: (d) => (d.dealsCompleted && d.dealsCompleted > 0) ? (d.dealValue || 0) / d.dealsCompleted : 0,
    },
    {
      title: 'Listings Acquired',
      dataKey: 'listingsAcquired',
      borderColor: COLORS.listings.border,
      backgroundColor: COLORS.listings.bg,
      fill: true,
      yAxisLabel: 'Listings'
    },
    {
      title: 'Avg. Time to First Action (Hours)',
      dataKey: 'avgTimeToFirstAction', // Custom mapper
      borderColor: COLORS.timeToFirstAction.border,
      backgroundColor: COLORS.timeToFirstAction.bg,
      fill: false,
      yAxisLabel: 'Hours',
      dataMap: (d) => d.avgTimeToFirstAction ? parseFloat((d.avgTimeToFirstAction / (1000 * 60 * 60)).toFixed(1)) : 0,
    },
    {
      title: 'Activities Completed',
      dataKey: 'activitiesCompleted',
      borderColor: COLORS.activities.border,
      backgroundColor: COLORS.activities.bg,
      fill: true,
      yAxisLabel: 'Activities'
    },
  ];

  return (
    <Card className="shadow-sm border-0" >
      <CardBody className="p-sm-3 p-md-4 p-lg-5">
        <CardTitle tag="h4" className="mb-5 text-center text-primary fw-bold" >
          Performance Trends ({period.charAt(0).toUpperCase() + period.slice(1)})
        </CardTitle>
        <Row className="g-4">
          {chartConfigs.map(config => (
            <Col md="6" xl="4" key={config.title}>
              <div className="chart-wrapper  p-3 shadow-sm" style={{ borderRadius: '0.5rem', height: '370px', background: '#fffffb' }}>
                <h6 className="text-secondary fw-semibold mb-3 text-center" style={{ color: '#4A5568' /* Gray-700 */ }}>
                  {config.title}
                </h6>
                <div style={{ height: 'calc(100% - 45px)' }}> {/* Adjusted for h6 height and margin */}
                  <Line
                    options={getChartOptions(config.yAxisLabel)} // Pass yAxisLabel to options
                    data={{
                      labels,
                      datasets: [{
                        label: config.title.includes('Avg.') ? config.title.split('(')[0].trim() : config.title, // Shorter label for legend
                        data: historyData.map(d => config.dataMap ? config.dataMap(d) : (d[config.dataKey] || 0)),
                        borderColor: config.borderColor,
                        backgroundColor: config.backgroundColor,
                        pointBackgroundColor: config.borderColor,
                        pointBorderColor: COLORS.white,
                        pointHoverBackgroundColor: COLORS.white,
                        pointHoverBorderColor: config.borderColor,
                        fill: config.fill,
                      }],
                    }}
                  />
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </CardBody>
    </Card>
  );
};

export default PerformanceTrends;
